import React from "react";
import { Box, Container, Grid, Typography } from "@mui/material";

import { ReactComponent as ComputerSelectTestSvg } from "assets/images/homepage/computer_select_test.svg";
import { ReactComponent as ComputerAnalyticsSvg } from "assets/images/technology-platform/computer_analytics.svg";
import { ReactComponent as ComputerProcessSvg } from "assets/images/technology-platform/computer_process.svg";
import {
  Acronym,
  AMP,
  CTDNA,
  DNA,
  LIMS,
  NGS,
  PCR,
  RNA,
} from "components/acronyms";
import Link from "components/shared/Link";
import { IDT_REQUEST_CONSULTATION } from "helpers/constants";
import { colors } from "theme";

import { PlatformItem } from "./PlatformItem";
import { PlatformItemProps } from "./type";

const KeyFeatureItems: PlatformItemProps[] = [
  {
    title: "AMP chemistry: Engineered for reliability",
    content: (
      <>
        Anchored Multiplex <PCR /> (AMP<sup>™</sup>) target enrichment chemistry
        is at the core of our kitted oncology products.
        <br />
        <br /> <AMP /> is a robust platform to create target-enriched <NGS />{" "}
        libraries by utilizing nidirectional gene-specific primers (
        <Acronym text="GSPs" />) that enrich for both known and unknown
        mutations. Adapters that contain molecular barcodes and sample indexes
        permit quantitative multiplex data analysis, read deduplication and
        accurate detection of oncogenic drivers.
      </>
    ),
    FeatureImage: ComputerProcessSvg,
  },
  {
    title: "Purpose-built analysis platform",
    content: (
      <>
        All kitted <NGS /> research assays are supported by an integrated
        analysis platform, Archer<sup>®</sup> Analysis, providing a turn-key
        solution to complex genomic problems.
        <Box component="div" mt={3}>
          <Box fontWeight={600}>One powerful platform, endless potential</Box>
          <Box
            component="ul"
            style={{ listStyleType: "disc", paddingLeft: 26 }}
          >
            {[
              "Simple and intuitive web-based interface",
              "In-line visualization for clear reporting",
              <>
                Integrates <LIMS /> data and third party providers
              </>,
              "Deploys securely to a cloud or local server",
            ].map((item: string | React.ReactNode, index: number) => (
              <Typography variant="inherit" component="li" key={index}>
                {item}
              </Typography>
            ))}
          </Box>
        </Box>
        <Link
          href={IDT_REQUEST_CONSULTATION}
          color="secondary"
          underline="always"
          target="_blank"
        >
          Request demo data
        </Link>{" "}
        to experience it for yourself.
      </>
    ),
    FeatureImage: ComputerAnalyticsSvg,
  },
  {
    title: "Custom solutions",
    content: (
      <>
        Adaptable assays for tomorrow’s needs
        <br />
        <br /> Our assays are modular and can grow and scale as your laboratory
        evolves over time. <AMP /> primers function independently, meaning new
        gene content can easily be added to existing panels without compromising
        assay performance.
        <Box
          component="ul"
          style={{ listStyleType: "disc", paddingLeft: 26, marginTop: 23 }}
        >
          {[
            <>
              Find the right assay by browsing our catalog of targeted <AMP />{" "}
              chemistry-based panels, or build your own <RNA />, <DNA />, or{" "}
              <CTDNA /> panel using the{" "}
              <Link url="/" color="secondary" underline="always">
                Assay Marketplace
              </Link>
            </>,
            "Run your assays with confidence by leveraging our predesigned, functionally tested primers for your targets of interest",
            "Scale your assays by iterating and updating your designs with additional targets as your needs",
          ].map((item: string | React.ReactNode, index: number) => (
            <Typography variant="inherit" component="li" key={index}>
              {item}
            </Typography>
          ))}
        </Box>
      </>
    ),
    FeatureImage: ComputerSelectTestSvg,
  },
];

export const OnePlatform = () => (
  <Box
    component="section"
    position="relative"
    bgcolor={colors.white}
    pt={{ xs: 3, sm: 4, md: 5, lg: 10 }}
    pb={{ xs: 4, sm: 5, md: 7, lg: 12 }}
  >
    <Container>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        rowGap={{ xs: 2, sm: 4 }}
        position="relative"
        maxWidth={{ sm: 428, md: "unset" }}
        margin="auto"
      >
        <Typography variant="h1" component="h2" align="center">
          One platform. Endless potential.
        </Typography>
        <Grid container spacing={3} justifyContent="center" component="ul">
          {KeyFeatureItems.map((item: PlatformItemProps, index: number) => (
            <Grid item xs={12} md={4} key={index} component="li">
              <PlatformItem {...item} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  </Box>
);
